import { useRef, useState } from "react";
import axios from "axios";
import { makeForm, portalChunks } from "@/lib/upload";
import { CheckCircle2, HelpCircle, Loader2, Upload, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 10;
// O resto da página do cliente está só em português: o inglês destes textos liga-se com ?lang=en no link.
export const LANG = new URLSearchParams(window.location.search).get("lang") === "en" ? "en" : "pt";
const TXT = {
  pt: {
    title: "Enviar vários documentos de uma vez", drop: "Arraste os ficheiros para aqui ou clique para escolher", pick: "Escolher ficheiros",
    hint: `Até ${MAX} ficheiros de uma vez`, max: `Máximo de ${MAX} ficheiros por envio.`, sending: (n) => `A enviar ${n} ${n === 1 ? "ficheiro" : "ficheiros"}…`,
    unknown: (n) => `${n} ${n === 1 ? "ficheiro não reconhecido" : "ficheiros não reconhecidos"}`, error: "Não foi possível enviar. Tente novamente.",
    which: "A que documento pertence?", choose: "Escolher documento", ok: "Confirmar", review: "Não reconhecemos estes ficheiros. Indique o documento ou deixe para a agência.",
    photo: "Tirar foto", file: "Escolher ficheiro",
  },
  en: {
    title: "Send several documents at once", drop: "Drag files here or click to choose", pick: "Choose files",
    hint: `Up to ${MAX} files at once`, max: `Up to ${MAX} files per upload.`, sending: (n) => `Sending ${n} ${n === 1 ? "file" : "files"}…`,
    unknown: (n) => `${n} ${n === 1 ? "file not recognised" : "files not recognised"}`, error: "Could not send. Please try again.",
    which: "Which document is this?", choose: "Choose document", ok: "Confirm", review: "We could not recognise these files. Pick the document or leave it to the agency.",
    photo: "Take photo", file: "Choose file",
  },
};
export const T = TXT[LANG];

function Summary({ results }) {
  const placed = results.filter((r) => r.status === "colocado");
  const unknown = results.filter((r) => r.status === "recebido_por_classificar").length;
  const failed = results.filter((r) => r.status === "erro" || r.status === "rejeitado");
  return (
    <ul className="mt-3 space-y-1 text-sm" data-testid="portal-bulk-summary">
      {placed.map((r, i) => <li key={`p${i}`} className="flex items-center gap-1.5 text-green-700"><CheckCircle2 size={15} /> {r.name || r.filename}</li>)}
      {unknown > 0 && <li className="flex items-center gap-1.5 text-amber-700" data-testid="portal-bulk-unknown"><HelpCircle size={15} /> {T.unknown(unknown)}</li>}
      {failed.map((r, i) => <li key={`f${i}`} className="flex items-start gap-1.5 text-red-700"><XCircle size={15} className="mt-0.5 shrink-0" /> {r.filename}: {r.line}</li>)}
    </ul>
  );
}

export function BulkUpload({ token, session, onDone }) {
  const input = useRef();
  const [busy, setBusy] = useState(0);
  const [over, setOver] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const send = async (list) => {
    const files = [...list].filter(Boolean);
    if (!files.length) return;
    if (files.length > MAX) { setErr(T.max); return; }
    setErr(null); setRes(null); setBusy(files.length);
    try {
      const fd = await makeForm(files.map((f) => ["files", f]), portalChunks(token, session));
      const { data } = await axios.post(`${API}/portal/${token}/upload-batch`, fd, { headers: { "X-Portal-Session": session } });
      setRes(data.results); onDone();
    } catch (ex) {
      const det = ex.response?.data?.detail;
      setErr(typeof det === "string" ? det : T.error);
    } finally { setBusy(0); }
  };
  const drop = (e) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files); };
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid="portal-bulk">
      <div className="font-semibold brand-text">{T.title}</div>
      <button type="button" onClick={() => input.current?.click()} disabled={!!busy} onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)} onDrop={drop} data-testid="portal-bulk-drop"
        className={`hidden md:flex mt-3 w-full flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed py-6 transition-colors ${over ? "border-slate-500 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
        {busy ? <Loader2 size={20} className="animate-spin text-slate-500" /> : <Upload size={20} className="text-slate-500" />}
        <span className="text-sm text-slate-700">{busy ? T.sending(busy) : T.drop}</span>
        <span className="text-xs text-slate-400">{T.hint}</span>
      </button>
      <button type="button" onClick={() => input.current?.click()} disabled={!!busy} data-testid="portal-bulk-pick"
        className="md:hidden mt-3 w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg border-2 border-slate-200 font-semibold text-sm brand-text disabled:opacity-60">
        {busy ? <><Loader2 size={16} className="animate-spin" /> {T.sending(busy)}</> : <><Upload size={16} /> {T.pick}</>}
      </button>
      <p className="md:hidden mt-1.5 text-xs text-slate-500 text-center">{T.hint}</p>
      <input ref={input} type="file" multiple accept=".pdf,image/*" className="hidden" data-testid="portal-bulk-input"
        onChange={(e) => { const f = e.target.files; send(f); e.target.value = ""; }} />
      {err && <div className="mt-2 text-sm text-red-600" data-testid="portal-bulk-error">{err}</div>}
      {res && <Summary results={res} />}
    </div>
  );
}

function AssignRow({ token, session, f, docs, onDone }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = async () => {
    setBusy(true);
    try { await axios.post(`${API}/portal/${token}/files/${f.id}/assign`, { key }, { headers: { "X-Portal-Session": session } }); onDone(); }
    catch { setBusy(false); }
  };
  return (
    <li className="py-2.5" data-testid={`portal-unrec-${f.id}`}>
      <div className="text-sm font-medium text-slate-800 break-all">{f.filename}</div>
      <div className="mt-1.5 flex gap-2">
        <select value={key} onChange={(e) => setKey(e.target.value)} aria-label={T.which} data-testid={`portal-unrec-select-${f.id}`}
          className="flex-1 min-w-0 h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          <option value="">{T.choose}</option>
          {docs.map((d) => <option key={d.key} value={d.key}>{d.client_name}</option>)}
        </select>
        <button onClick={ok} disabled={!key || busy} data-testid={`portal-unrec-ok-${f.id}`}
          className="h-10 px-4 rounded-lg text-white text-sm font-semibold bg-slate-800 disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : T.ok}</button>
      </div>
    </li>
  );
}

export function Unrecognized({ token, session, files, docs, onDone }) {
  if (!files?.length) return null;
  const open = docs.filter((d) => ["falta", "pedido", "problema"].includes(d.state));
  return (
    <div className="bg-amber-50 rounded-xl border border-amber-100 p-4" data-testid="portal-unrecognized">
      <div className="font-semibold text-amber-900">{T.unknown(files.length)}</div>
      <p className="text-sm text-amber-900/80 mt-0.5">{T.review}</p>
      <ul className="divide-y divide-amber-100">{files.map((f) => <AssignRow key={f.id} token={token} session={session} f={f} docs={open} onDone={onDone} />)}</ul>
    </div>
  );
}
